import type {AddonFamilyId} from '@packages/domains/billing/types';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import type {EventName, EventData} from '@packages/shared/segment-typewriter';

declare global {
  interface Window {
    analytics: any;
  }
}

// data is a freeform dict object in Segment, see
// https://segment.com/docs/sources/website/analytics.js/#track
export type Event<K extends EventName> = {
  name: K;
  data?: EventData<K>;
  opts?: {
    [key: string]: any;
  };
  callback?: () => void;
};

// intended for tool usage such as DataDog; object shape differs from Event
export type RUMEvent = {
  value?: string | number;
  from?: string;
  action?: string;
  callback?: () => void;
};

const anonymousIdCallbacks: Array<
  any | ((arg1?: string | null | undefined) => void)
> = [];
let cannotInitialize = false;

export const initAnalytics = (): Promise<{
  ready: boolean;
}> => {
  return new Promise(
    (
      resolve: (
        result:
          | Promise<{
              ready: boolean;
            }>
          | {
              ready: boolean;
            }
      ) => void,
      reject: (error?: any) => void
    ) => {
      if (!window.analytics) {
        return reject(new Error('Analytics not defined'));
      }

      // Dispatch anonymousIds when analytics is ready
      // OR after 5 seconds if analytics doesn't load
      // (possibly blocked  by ads blockers)
      const timeout = setTimeout(() => {
        cannotInitialize = true;
        dispatchAnonymousId();
        // We don't want to reject here because we want this to fail silently
        // without logging an error. We know people use ad blockers and we don't
        // need to log an error for it.
        resolve({ready: false});
      }, 5000);

      window.analytics.ready(() => {
        dispatchAnonymousId();
        // Clear timeout to make sure we don't dispatch twice
        clearTimeout(timeout);
        resolve({ready: true});
      });
    }
  );
};

/**
 * To be GDPR compliant we want to use the analytics module from the top window, since we don't show consent banner in the iframe.
 */
function getGDPRAnalytics() {
  try {
    if (window.top !== window && window.top?.analytics) {
      return window.top.analytics;
    }

    return window.analytics;
  } catch {
    return window.analytics;
  }
}

export const trackEvent = <K extends EventName>(event: Event<K>) => {
  if (process.env.NODE_ENV !== 'production') {
    console.info('Analytics Event:', event);
  }

  if (process.env.NODE_ENV === 'test') {
    typeof event.callback === 'function' && event.callback();
    return;
  }

  if (!getGDPRAnalytics()) {
    console.error(
      'Cannot track an event, because analytics module is missing from a global scope'
    );
    typeof event.callback === 'function' && event.callback();
    return;
  }

  let callbackCalled = false;

  if (!getGDPRAnalytics()?.initialized) {
    /**
     * If analytics lib is not initialized it can be one of two cases:
     * (1) lib is still loading (usually the case for events tracked on page load)
     * (2) ads-blockers blocked it and it won't finish loading
     *
     * We want to make sure to always call the callback (only once!),
     * even if analytics lib won't finish loading because of ad-blockers.
     *
     * Given that we're unable to distinguish the two cases, we're treating
     * them equally and adding an early call to the callback.
     *
     * NOTE: the most common usage for callbacks is when we need to track an
     * event prior to a redirect and we want to avoid a race condition. Example:
     *   trackEvent({
     *     name: 'Event Name',
     *     callback: () => { window.open(url); }
     *   });
     *
     * This implementation introduces a possible race condition between the
     * redirect and the event being fired, in the case of page load events.
     * However there's currently no such use case so we're comfortable with
     * tradeoff.
     */
    typeof event.callback === 'function' && event.callback();
    callbackCalled = true;
  }

  getGDPRAnalytics().track(
    event.name,
    event.data || {},
    event.opts || {},
    function () {
      if (!callbackCalled && typeof event.callback === 'function') {
        event.callback();
      }
    }
  );
};

export const aliasUser = (identifier: string): any => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }
  window.analytics.alias(identifier);
};

export const identifyUser = (
  identifier: string,
  traits: {
    [key: string]: unknown;
  }
): any => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  window.analytics.identify(identifier, traits);
};

export const getAnonymousId = (
  onReadyCallback?: ((arg1?: string | null | undefined) => void) | null
): string | null | undefined => {
  if (!onReadyCallback) {
    return isInitialized() && !cannotInitialize
      ? window.analytics.user().anonymousId()
      : undefined;
  }

  if (isInitialized()) {
    onReadyCallback(window.analytics.user().anonymousId());
  } else if (cannotInitialize) {
    onReadyCallback(undefined);
  } else {
    anonymousIdCallbacks.push(onReadyCallback);
  }
};

function dispatchAnonymousId() {
  const callbacks = anonymousIdCallbacks.length;
  if (callbacks === 0) {
    return;
  }

  const anonymousId = isInitialized()
    ? window.analytics.user().anonymousId()
    : undefined;

  for (let i = 0; i < callbacks; i++) {
    const cb = anonymousIdCallbacks[i];
    cb(anonymousId);
  }
}

function isInitialized() {
  return window.analytics?.initialized;
}

// Paywall related events. See more here: https://webflow.atlassian.net/wiki/spaces/LIF/pages/239601028/Paywalls+analytics+guidelines

type CheckoutFlow =
  | 'multi-sku'
  | 'site'
  | 'workspace'
  | 'workspace seats'
  | AddonFamilyId;
type SurfaceArea = 'designer' | 'dashboard' | 'marketplace' | 'website';

type PaywallEncountered = {
  'checkout flow'?: CheckoutFlow;
  'entitlement id': string;
  'entitlement scope': Array<'workspace' | 'site'>;
  page: string;
  'paywall name': string;
  'paywall trigger name': string;
  /** Recommended site plan ID from upgrade recommendation logic (when available) */
  'selected site plan id'?: string;
  /** Recommended workspace plan ID from upgrade recommendation logic (when available) */
  'selected workspace plan id'?: string;
  'site id': string;
  'site plan id'?: string;
  'surface area': SurfaceArea;
  'workspace id': string;
  'workspace plan id'?: string;
};

export const trackPaywallEncountered = (data: PaywallEncountered) => {
  const sitePlanId = data['site plan id'] || 'starter';
  const workspacePlanId = data['workspace plan id'] || 'starter';

  trackEvent({
    name: ANALYTICS_EVENTS.PAYWALL_ENCOUNTERED,
    data: {
      ...data,
      'site plan id': sitePlanId,
      'workspace plan id': workspacePlanId,
    },
  });
};

type PricingCTAClicked = {
  'surface area': SurfaceArea;
  page: string;
  'paywall name'?: string;
  'paywall trigger name'?: string;
  'site id': string;
  'site plan id'?: string;
  'workspace id': string;
  'workspace plan id'?: string;
  'entitlement id': string;
  'entitlement scope': Array<'workspace' | 'site'>;
  'cta action':
    | 'View Site Plans'
    | 'Contact Account Team'
    | 'View Add-ons Plans'
    | 'View Workspace Plans'
    | 'Open Enterprise Modal'
    | 'View Enterprise Page'
    | 'Open Checkout Modal'
    | 'Cancel'
    | 'Upgrade site plan';
};

export const trackPricingCTAClicked = (data: PricingCTAClicked) => {
  const sitePlanId = data['site plan id'] || 'starter';
  const workspacePlanId = data['workspace plan id'] || 'starter';

  trackEvent({
    name: ANALYTICS_EVENTS.PRICING_CTA_CLICKED,
    data: {
      ...data,
      'site plan id': sitePlanId,
      'workspace plan id': workspacePlanId,
    },
  });
};
